import React from 'react';
import { keyframes } from '@mui/system';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Chip,
  Grid,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  Skeleton,
  Checkbox,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';
import { useGetProductsQuery, useGetProductFeaturesQuery, useUpdateProductTiersFeaturesMutation } from '../../app/services/product';
import { useGetProductAddOnsQuery, useMarkProductFeatureAsAddOnMutation, useUnmarkProductFeatureAsAddOnMutation } from '../../app/services/addons';
import { useGetProductSubscriptionsQuery, useGetSubscriptionFeaturesQuery, useUpdateSubscriptionFeaturesMutation } from '../../app/services/subscription';
import type { ProductSubscriptionFeatureDto } from '../models/subscription';

const pulseAnim = keyframes({
  '0%': { transform: 'scale(1)', opacity: 0.5 },
  '100%': { transform: 'scale(1.06)', opacity: 0 },
});

const TIER_ACCENTS = ['#64B5F6', '#81C784', '#FFCC80', '#BA68C8', '#80DEEA'];
const TIER_ACCENTS_RGBA = [
  'rgba(100,181,246,0.35)',
  'rgba(129,199,132,0.35)',
  'rgba(255,204,128,0.35)',
  'rgba(186,104,200,0.35)',
  'rgba(128,222,234,0.35)'
];

// Hoisted to module scope so its component identity is stable across parent renders
// (defining it inside ProductsPage caused every render to remount every loader).
const TierFeaturesLoader: React.FC<{
  subscriptionId: string;
  onLoaded: (subscriptionId: string, features: ProductSubscriptionFeatureDto[]) => void;
}> = React.memo(({ subscriptionId, onLoaded }) => {
  const { data, isLoading } = useGetSubscriptionFeaturesQuery(subscriptionId, { skip: !subscriptionId });
  React.useEffect(() => {
    if (data) onLoaded(subscriptionId, data);
  }, [data, subscriptionId, onLoaded]);
  return isLoading ? <Skeleton variant="text" width={120} /> : null;
});

const ProductsPage: React.FC = () => {
  const { data: productsData, isLoading: loadingProducts } = useGetProductsQuery({ page: 1, pageSize: 500 }, { refetchOnMountOrArgChange: true });
  const { data: productSubscriptions, isLoading: loadingSubs } = useGetProductSubscriptionsQuery();
  const [updateProductTiersFeatures, { isLoading: savingTier }] = useUpdateProductTiersFeaturesMutation();
  const [updateSubscriptionFeatures] = useUpdateSubscriptionFeaturesMutation();

  const products: Product[] = (productsData?.data || []).filter((p: any) => p.productName !== 'IDMSPortal');

  const [selectedProduct, setSelectedProduct] = React.useState<Product | null>(null);
  const [productInput, setProductInput] = React.useState<string>('');
  // Preselect TargetCRM on load if available
  const didInitRef = React.useRef(false);
  React.useEffect(() => {
    // Only perform automatic preselection once on initial load
    if (!didInitRef.current && products && products.length > 0) {
      const target = products.find((p: any) => (p.productName || '').toLowerCase() === 'targetcrm');
      const initial = target || products[0];
      setSelectedProduct(initial);
      setProductInput(initial?.productName || '');
      didInitRef.current = true;
    }
  }, [products]);

  const tiers = React.useMemo(() => {
    if (!selectedProduct || !productSubscriptions) return [] as { id: string; name: string }[];
    const entry = productSubscriptions.find(p => p.productId === selectedProduct.id);
    if (!entry) return [];
    return (entry.subscriptions || []).map(s => ({ id: s.productSubscriptionId, name: s.subscriptionName }));
  }, [selectedProduct, productSubscriptions]);

  const [assignedByTier, setAssignedByTier] = React.useState<Record<string, string[]>>({}); 
  const [initialAssignedByTier, setInitialAssignedByTier] = React.useState<Record<string, string[]>>({});

  // Stable so TierFeaturesLoader's effect doesn't re-fire every parent render.
  const handleTierFeaturesLoaded = React.useCallback(
    (subscriptionId: string, features: ProductSubscriptionFeatureDto[]) => {
      const ids = features.map(f => f.productFeatureId);
      const sameIds = (prev?: string[]) =>
        !!prev && prev.length === ids.length && prev.every((v, i) => v === ids[i]);
      setInitialAssignedByTier(prev =>
        sameIds(prev[subscriptionId]) ? prev : { ...prev, [subscriptionId]: ids }
      );
      setAssignedByTier(prev =>
        prev[subscriptionId] ? prev : { ...prev, [subscriptionId]: ids }
      );
    },
    []
  );

  const [selection, setSelection] = React.useState<Record<string, Set<string>>>({}); 
  const [selectionScope, setSelectionScope] = React.useState<string | null>(null);
  const toggleSelection = (columnId: string, featureId: string) => {
    setSelection(prev => {
      let next: Record<string, Set<string>> = prev;
      if (selectionScope && selectionScope !== columnId) {
        next = {};
      }
      const curr = new Set(next[columnId] || []);
      if (curr.has(featureId)) curr.delete(featureId); else curr.add(featureId);
      setSelectionScope(columnId);
      return { ...next, [columnId]: curr };
    });
  };
  const hasSelection = React.useMemo(() => Object.values(selection).some(s => (s?.size || 0) > 0), [selection]);
  const selectedCount = React.useMemo(() => Object.values(selection).reduce((n, s) => n + (s?.size || 0), 0), [selection]);

  const selectionScopeLabel = React.useMemo(() => {
    if (!selectionScope) return '';
    if (selectionScope === 'available') return 'Available';
    const tier = (tiers || []).find(t => t.id === selectionScope);
    return tier ? tier.name : selectionScope;
  }, [selectionScope, tiers]);

  const [filters, setFilters] = React.useState<Record<string, string>>({}); // key: tierId|'unassigned'
  const setFilter = (key: string, val: string) => setFilters(prev => ({ ...prev, [key]: val }));

  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});
  const toggleCollapsed = (key: string) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  React.useEffect(() => {
    if ((tiers || []).length > 3) {
      const next: Record<string, boolean> = {};
      tiers.forEach((t, idx) => { next[t.id] = idx >= 3; });
      setCollapsed(next);
    }
  }, [tiers]);

  const [dragOverTier, setDragOverTier] = React.useState<string | null>(null);
  const [pulseTier, setPulseTier] = React.useState<string | null>(null);

  const boardRef = React.useRef<HTMLDivElement | null>(null);
  const scrollBoard = (dir: 'left' | 'right') => {
    const el = boardRef.current; if (!el) return;
    const delta = Math.round(el.clientWidth * 0.8);
    el.scrollBy({ left: dir === 'left' ? -delta : delta, behavior: 'smooth' });
  };

  const { data: productFeaturesData } = useGetProductFeaturesQuery(
    selectedProduct ? { productId: selectedProduct.id, page: 1, pageSize: 500 } : ({} as any),
    { skip: !selectedProduct }
  );
  const { data: addonsData, refetch: refetchAddons } = useGetProductAddOnsQuery(selectedProduct ? selectedProduct.id : '', { skip: !selectedProduct });
  const featureCatalog = React.useMemo(() => {
    const map = new Map<string, { name: string; description?: string }>();
    const list = (productFeaturesData as any)?.data as Feature[] | undefined;
    (list || []).forEach((f) => map.set(f.id, { name: f.productFeatureName, description: f.description }));
    return map;
  }, [productFeaturesData]);
  const available: string[] = React.useMemo(() => {
    const list = (productFeaturesData as any)?.data as Feature[] | undefined;
    if (!list) return [];
    return list.map(f => f.id);
  }, [productFeaturesData]);
  const addOnIdSet = React.useMemo(() => {
    const set = new Set<string>();
    (addonsData || []).forEach((a: any) => { if (a?.featureId) set.add(a.featureId); });
    return set;
  }, [addonsData]);
  const [markAsAddOn] = useMarkProductFeatureAsAddOnMutation();
  const [unmarkAddOn] = useUnmarkProductFeatureAsAddOnMutation();
  const [addOnsOpen, setAddOnsOpen] = React.useState(false);
  const addOnCount = (addonsData || []).length || 0;
  const visibleAvailableCount = React.useMemo(() => {
    return available.filter(fid => !addOnIdSet.has(fid)).length;
  }, [available, addOnIdSet]);

  React.useEffect(() => {
    if (!addOnIdSet || addOnIdSet.size === 0) return;
    setAssignedByTier(prev => {
      let changed = false;
      const next: Record<string, string[]> = {};
      Object.keys(prev).forEach(tid => {
        const filtered = (prev[tid] || []).filter(id => !addOnIdSet.has(id));
        if (filtered.length !== (prev[tid] || []).length) changed = true;
        next[tid] = filtered;
      });
      return changed ? next : prev;
    });
  }, [addOnIdSet]);

  React.useEffect(() => {
    if (!featureCatalog || featureCatalog.size === 0) return;
    setAssignedByTier(prev => {
      let changed = false;
      const next: Record<string, string[]> = {};
      Object.keys(prev).forEach(tid => {
        const filtered = (prev[tid] || []).filter(id => featureCatalog.has(id));
        if (filtered.length !== (prev[tid] || []).length) changed = true;
        next[tid] = filtered;
      });
      return changed ? next : prev;
    });
  }, [featureCatalog]);


  const isDirty = React.useMemo(() => {
    const keys = new Set([...Object.keys(assignedByTier), ...Object.keys(initialAssignedByTier)]);
    for (const k of keys) {
      const a = new Set(assignedByTier[k] || []);
      const b = new Set(initialAssignedByTier[k] || []);
      if (a.size !== b.size) return true;
      for (const id of a) if (!b.has(id)) return true;
    }
    return false;
  }, [assignedByTier, initialAssignedByTier]);

  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuCtx, setMenuCtx] = React.useState<{ columnId: string; featureId: string } | null>(null);
  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, ctx: { columnId: string; featureId: string }) => { setMenuAnchor(e.currentTarget); setMenuCtx(ctx); };
  const closeMenu = () => { setMenuAnchor(null); setMenuCtx(null); };

  const handleSave = async () => {
    if (!selectedProduct) return;
    // Only include tiers whose current features have been loaded; otherwise we'd
    // send an empty featureIds array for an in-flight tier and wipe it server-side.
    const loadedTiers = tiers.filter(t => Object.prototype.hasOwnProperty.call(initialAssignedByTier, t.id));
    try {
      const payload = loadedTiers.map(t => ({
        subscriptionId: t.id,
        featureIds: (assignedByTier[t.id] || []).filter(fid => featureCatalog.has(fid))
      }));
      await updateProductTiersFeatures({ productId: selectedProduct.id, tiers: payload }).unwrap();
      setInitialAssignedByTier(assignedByTier);
      toast.success('Changes saved');
    } catch (e: any) {
      const status = e?.status || e?.originalStatus;
      if (status === 404 || status === 405) {
        try {
          for (const t of loadedTiers) {
            const featureIds = (assignedByTier[t.id] || []).filter(fid => featureCatalog.has(fid));
            await updateSubscriptionFeatures({ subscriptionId: t.id, productId: selectedProduct.id, featureIds }).unwrap();
          }
          setInitialAssignedByTier(assignedByTier);
          toast.success('Changes saved');
        } catch (inner: any) {
          const msg = inner?.data?.title || inner?.data || inner?.message || 'Failed to save changes';
          toast.error(typeof msg === 'string' ? msg : 'Failed to save changes');
        }
      } else {
        const msg = e?.data?.title || e?.data || e?.message || 'Failed to save changes';
        toast.error(typeof msg === 'string' ? msg : 'Failed to save changes');
      }
    }
  };

  const [destination, setDestination] = React.useState<string>('');
  React.useEffect(() => {
    if (!destination && tiers.length > 0) setDestination(tiers[0].id);
  }, [tiers, destination]);
  const moveAllSelectedTo = (toId: string) => {
    if (!toId) return;
    const allSelected = new Set<string>();
    Object.values(selection).forEach(s => s?.forEach(id => allSelected.add(id)));
    if (allSelected.size === 0) return;
    setAssignedByTier(prev => {
      const next: Record<string, string[]> = { ...prev };
      const dest = new Set(next[toId] || []);
      allSelected.forEach(id => dest.add(id));
      next[toId] = Array.from(dest);
      return next;
    });
    setSelection({});
    setSelectionScope(null);
  };

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
        {loadingProducts ? (
          <Skeleton variant="rounded" width={360} height={40} />
        ) : (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: 1, minWidth: 280 }}>
              <Autocomplete
                options={products}
                getOptionLabel={(o) => o.productName}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedProduct}
                inputValue={productInput}
                onInputChange={(_e, newInputValue, reason) => {
                  setProductInput(newInputValue);
                  if (reason === 'clear' || newInputValue === '') {
                    setSelectedProduct(null);
                    setAssignedByTier({});
                    setInitialAssignedByTier({});
                    setSelection({});
                  }
                }}
                onChange={(_e, v) => {
                  setSelectedProduct(v);
                  setProductInput(v?.productName || '');
                  setAssignedByTier({});
                  setInitialAssignedByTier({});
                  setSelection({});
                }}
                disableClearable={false}
                clearOnEscape
                renderInput={(params) => <TextField {...params} label="Select product" placeholder="Choose a product" />}
              />
            </Box>
            <Tooltip title={isDirty ? 'Save all staged changes' : 'No changes to save'}>
              <span>
                <Button variant="contained" startIcon={<SaveIcon />} disabled={!isDirty || savingTier} onClick={handleSave}>
                  Save
                </Button>
              </span>
            </Tooltip>
          </Stack>
        )}
      </Paper>

      {(!selectedProduct || loadingSubs) ? (
        <Skeleton variant="rounded" height={320} />
      ) : tiers.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography>No subscription tiers found for this product.</Typography>
        </Paper>
      ) : (
        <>
          {/* Scroll controls */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">Tiers</Typography>
              <Chip size="small" label={tiers.length} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="text" onClick={() => setAddOnsOpen(true)} disabled={!selectedProduct} sx={{ mr: 1 }}>
                Manage Add-Ons
                {addOnCount > 0 && (<Chip size="small" sx={{ ml: 1 }} label={addOnCount} />)}
              </Button>
              <Tooltip title="Scroll left"><span><IconButton size="small" onClick={() => scrollBoard('left')}><ChevronLeftIcon /></IconButton></span></Tooltip>
              <Tooltip title="Scroll right"><span><IconButton size="small" onClick={() => scrollBoard('right')}><ChevronRightIcon /></IconButton></span></Tooltip>
            </Stack>
          </Stack>

          <Box ref={boardRef} sx={{ overflowX: 'auto', pb: 1 }}>
            <Grid container wrap={'nowrap'} columnSpacing={2.5} rowSpacing={2.25} sx={{ minHeight: 360 }}>
              <Grid item sx={{ flex: '0 0 320px' }}>
                <Paper variant="outlined" sx={{ p: 1.5, height: '100%', minHeight: 320, bgcolor: (theme) => theme.palette.action.hover, borderColor: 'primary.light', borderLeft: (theme) => `3px solid ${theme.palette.primary.main}` }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>Available Features</Typography>
                    <Chip size="small" label={`${visibleAvailableCount}`} />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <TextField size="small" fullWidth placeholder="Filter" value={filters['available'] || ''} onChange={(e) => setFilter('available', e.target.value)} />
                    <Tooltip title="Select all filtered">
                      <Checkbox
                        checked={(() => {
                          const base = available.filter(fid => !addOnIdSet.has(fid));
                          const filtered = base.filter(fid => (featureCatalog.get(fid)?.name || fid).toLowerCase().includes((filters['available'] || '').toLowerCase()));
                          const sel = selection['available'] || new Set();
                          return filtered.length > 0 && filtered.every(id => sel.has(id));
                        })()}
                        indeterminate={(() => {
                          const base = available.filter(fid => !addOnIdSet.has(fid));
                          const filtered = base.filter(fid => (featureCatalog.get(fid)?.name || fid).toLowerCase().includes((filters['available'] || '').toLowerCase()));
                          const sel = selection['available'] || new Set();
                          const count = filtered.filter(id => sel.has(id)).length;
                          return count > 0 && count < filtered.length;
                        })()}
                        onChange={() => {
                          const base = available.filter(fid => !addOnIdSet.has(fid));
                          const filtered = base.filter(fid => (featureCatalog.get(fid)?.name || fid).toLowerCase().includes((filters['available'] || '').toLowerCase()));
                          setSelection(prev => {
                            const next: Record<string, Set<string>> = {};
                            const sel = new Set(prev['available'] || []);
                            const allSelected = filtered.length > 0 && filtered.every(id => sel.has(id));
                            if (allSelected) filtered.forEach(id => sel.delete(id));
                            else filtered.forEach(id => sel.add(id));
                            return { ...next, available: sel };
                          });
                          setSelectionScope('available');
                        }}
                        size="small"
                      />
                    </Tooltip>
                  </Stack>
                  <Divider sx={{ mb: 1 }} />
                  <Stack spacing={0.5}>
                    {available.length === 0 && (
                      <Typography variant="body2" color="text.secondary">No features</Typography>
                    )}
                    {available.filter(fid => (!addOnIdSet.has(fid)) && (featureCatalog.get(fid)?.name || fid).toLowerCase().includes((filters['available'] || '').toLowerCase())).map(fid => {
                      const label = featureCatalog.get(fid)?.name || fid;
                      return (
                      <Stack direction="row" key={`avail-${fid}`} alignItems="center" spacing={0.5}>
                        <Tooltip title={label} placement="top">
                          <Chip
                            size="small"
                            label={label}
                            variant={(selection['available'] || new Set()).has(fid) ? 'filled' : 'outlined'}
                            color={(selection['available'] || new Set()).has(fid) ? 'primary' : 'default'}
                            onClick={() => toggleSelection('available', fid)}
                            draggable
                            onDragStart={(e) => {
                              const sel = selection['available'] || new Set<string>();
                              const ids = sel.has(fid) ? Array.from(sel) : [fid];
                              e.dataTransfer.setData('text/feature-id', fid);
                              e.dataTransfer.setData('application/feature-ids', JSON.stringify(ids));
                              e.dataTransfer.setData('text/origin', 'available');
                              e.dataTransfer.effectAllowed = 'copy';
                            }}
                            sx={{
                              width: 160,
                              minWidth: 160,
                              maxWidth: 160,
                              flexShrink: 0,
                              justifyContent: 'flex-start',
                              '& .MuiChip-label': {
                                width: '100%',
                                display: 'inline-block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                        </Tooltip>
                        <IconButton size="small" onClick={(e) => openMenu(e as any, { columnId: 'available', featureId: fid })}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    );})}
                  </Stack>
                </Paper>
              </Grid>

              {tiers.map((t, idx) => (
                <Grid item key={t.id} sx={{ flex: `0 0 ${collapsed[t.id] ? 200 : 320}px` }}>
                  <Paper
                    variant="outlined"
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                    onDragEnter={() => setDragOverTier(t.id)}
                    onDragLeave={() => setDragOverTier(prev => (prev === t.id ? null : prev))}
                    onDrop={(e) => {
                      e.preventDefault();
                      const origin = e.dataTransfer.getData('text/origin');
                      let ids: string[] = [];
                      const json = e.dataTransfer.getData('application/feature-ids');
                      if (json) {
                        try { ids = JSON.parse(json); } catch { ids = []; }
                      }
                      if (!ids || ids.length === 0) {
                        const fid = e.dataTransfer.getData('text/feature-id');
                        if (fid) ids = [fid];
                      }
                      if (!ids || ids.length === 0) return;
                      if (origin && origin.startsWith('tier:')) {
                        const sourceTierId = origin.split(':')[1];
                        if (sourceTierId === t.id) { setDragOverTier(null); return; }
                      }
                      const currentDest = new Set(assignedByTier[t.id] || []);
                      let addedCount = 0;
                      ids.forEach(id => { if (!currentDest.has(id)) { currentDest.add(id); addedCount++; } });
                      if (addedCount === 0) { setDragOverTier(null); return; }
                      setAssignedByTier(prev => {
                        const next: Record<string, string[]> = { ...prev };
                        next[t.id] = Array.from(currentDest);
                        return next;
                      });
                      setDestination(t.id);
                      try { toast.success(`Assigned ${addedCount} feature${addedCount !== 1 ? 's' : ''} to ${t.name}`); } catch {}
                      setSelection({});
                      setSelectionScope(null);
                      setPulseTier(t.id);
                      window.setTimeout(() => setPulseTier(current => (current === t.id ? null : current)), 800);
                      setDragOverTier(null);
                    }}
                    sx={{
                      p: 1.5,
                      height: '100%',
                      minHeight: 320,
                      display: 'flex',
                      flexDirection: 'column',
                      borderLeft: `3px solid ${TIER_ACCENTS[idx % 5]}`,
                      outline: dragOverTier === t.id ? '2px dashed rgba(25,118,210,0.5)' : 'none',
                      outlineOffset: 2,
                      position: 'relative',
                      boxShadow: dragOverTier === t.id ? `0 0 0 3px ${TIER_ACCENTS_RGBA[idx % 5]}` : 'none',
                      '&::after': pulseTier === t.id ? {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 4,
                        border: `2px solid ${TIER_ACCENTS[idx % 5]}`,
                        animation: `${pulseAnim} 800ms ease-out`,
                        pointerEvents: 'none',
                      } : {},
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ['#64B5F6','#81C784','#FFCC80','#BA68C8','#80DEEA'][idx % 5] }} />
                        <Typography variant="subtitle1" fontWeight={700}>{t.name}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" color="primary" variant="outlined" label={`${(assignedByTier[t.id] || []).length}`} />
                        <IconButton size="small" onClick={() => toggleCollapsed(t.id)} aria-label={collapsed[t.id] ? 'Expand' : 'Collapse'}>
                          {collapsed[t.id] ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                        </IconButton>
                      </Stack>
                    </Stack>
                    <TierFeaturesLoader
                      subscriptionId={t.id}
                      onLoaded={handleTierFeaturesLoaded}
                    />
                    <Collapse in={!collapsed[t.id]} timeout="auto" unmountOnExit>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <TextField size="small" fullWidth placeholder="Filter" value={filters[t.id] || ''} onChange={(e) => setFilter(t.id, e.target.value)} />
                          <Tooltip title="Select all filtered">
                            <Checkbox
                              checked={(() => {
                                const filtered = (assignedByTier[t.id] || []).filter(fid => (featureCatalog.get(fid)?.name || fid).toLowerCase().includes((filters[t.id] || '').toLowerCase()));
                                const sel = selection[t.id] || new Set();
                                return filtered.length > 0 && filtered.every(id => sel.has(id));
                              })()}
                              indeterminate={(() => {
                                const filtered = (assignedByTier[t.id] || []).filter(fid => (featureCatalog.get(fid)?.name || fid).toLowerCase().includes((filters[t.id] || '').toLowerCase()));
                                const sel = selection[t.id] || new Set();
                                const count = filtered.filter(id => sel.has(id)).length;
                                return count > 0 && count < filtered.length;
                              })()}
                              onChange={() => {
                                const filtered = (assignedByTier[t.id] || []).filter(fid => (featureCatalog.get(fid)?.name || fid).toLowerCase().includes((filters[t.id] || '').toLowerCase()));
                                setSelection(prev => {
                                  // clear previous scope to enforce single-list selection
                                  const next: Record<string, Set<string>> = {};
                                  const sel = new Set(prev[t.id] || []);
                                  const allSelected = filtered.length > 0 && filtered.every(id => sel.has(id));
                                  if (allSelected) filtered.forEach(id => sel.delete(id));
                                  else filtered.forEach(id => sel.add(id));
                                  return { ...next, [t.id]: sel };
                                });
                                setSelectionScope(t.id);
                              }}
                              size="small"
                            />
                          </Tooltip>
                        </Stack>
                        <Divider sx={{ mb: 1 }} />
                        <Stack spacing={0.5} sx={{ flex: 1, overflow: 'auto', pb: 6 }}>
                          {(assignedByTier[t.id] || []).length === 0 && (
                            <Typography variant="body2" color="text.secondary">No features</Typography>
                          )}
                          {(assignedByTier[t.id] || []).filter(fid => (!addOnIdSet.has(fid)) && (featureCatalog.get(fid)?.name || fid).toLowerCase().includes((filters[t.id] || '').toLowerCase())).map(fid => {
                            const label = featureCatalog.get(fid)?.name || fid;
                            return (
                            <Stack direction="row" key={`${t.id}-${fid}`} alignItems="center" spacing={0.5}>
                              <Tooltip title={label} placement="top">
                                <Chip
                                  size="small"
                                  label={label}
                                  variant={(selection[t.id] || new Set()).has(fid) ? 'filled' : 'outlined'}
                                  color={(selection[t.id] || new Set()).has(fid) ? 'primary' : 'default'}
                                  onClick={() => toggleSelection(t.id, fid)}
                                  draggable
                                  onDragStart={(e) => {
                                    const sel = selection[t.id] || new Set<string>();
                                    const ids = sel.has(fid) ? Array.from(sel) : [fid];
                                    e.dataTransfer.setData('text/feature-id', fid);
                                    e.dataTransfer.setData('application/feature-ids', JSON.stringify(ids));
                                    e.dataTransfer.setData('text/origin', `tier:${t.id}`);
                                    e.dataTransfer.effectAllowed = 'copy';
                                  }}
                                  sx={{
                                    width: 160,
                                    minWidth: 160,
                                    maxWidth: 160,
                                    flexShrink: 0,
                                    justifyContent: 'flex-start',
                                    '& .MuiChip-label': {
                                      width: '100%',
                                      display: 'inline-block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    },
                                  }}
                                />
                              </Tooltip>
                              <IconButton size="small" onClick={(e) => openMenu(e as any, { columnId: t.id, featureId: fid })}>
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          );})}
                        </Stack>
                    </Collapse>
                    {collapsed[t.id] && (
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary">Collapsed</Typography>
                        <Typography variant="caption" color="text.secondary">{(assignedByTier[t.id] || []).length} features</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
          {hasSelection && (
            <Box sx={{ position: 'fixed', right: 24, bottom: 24, bgcolor: 'background.paper', boxShadow: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, zIndex: (theme) => theme.zIndex.modal - 1 }}>
              <Chip size="small" label={`${selectedCount} selected`} />
              {selectionScopeLabel && (
                <Tooltip title="Selections are limited to one list at a time.">
                  <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                    Selected from: {selectionScopeLabel}
                  </Typography>
                </Tooltip>
              )}
              <TextField select size="small" label="Assign to" value={destination} onChange={(e) => setDestination(e.target.value)} sx={{ minWidth: 180 }}>
                {tiers.map(t => (<MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>))}
              </TextField>
              <Button variant="contained" size="small" onClick={() => moveAllSelectedTo(destination)}>Assign</Button>
              <Button size="small" onClick={() => { setSelection({}); setSelectionScope(null); }}>Clear</Button>
            </Box>
          )}
        </>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {menuCtx && (
          <MenuItem onClick={async () => {
            if (!selectedProduct) return;
            const fid = menuCtx.featureId;
            try {
              await markAsAddOn({ productId: selectedProduct.id, featureId: fid }).unwrap();
              setAssignedByTier(prev => {
                const next: Record<string, string[]> = { ...prev };
                Object.keys(next).forEach(tid => {
                  const s = new Set(next[tid] || []); s.delete(fid); next[tid] = Array.from(s);
                });
                return next;
              });
              await refetchAddons();
              toast.success('Marked as add-on');
            } catch (err: any) {
              const msg = err?.data?.title || err?.data || err?.message || 'Failed to mark as add-on';
              toast.error(typeof msg === 'string' ? msg : 'Failed to mark as add-on');
            }
            closeMenu();
          }}>Mark as Add-On</MenuItem>
        )}
        {menuCtx && tiers.map(t => (
          <MenuItem key={t.id} onClick={() => {
            const fid = menuCtx.featureId;
            setAssignedByTier(prev => {
              const next: Record<string, string[]> = { ...prev };
              // Assign to destination tier (multi-tier allowed)
              const dest = new Set(next[t.id] || []); dest.add(fid); next[t.id] = Array.from(dest);
              return next;
            });
            closeMenu();
          }}>Assign to {t.name}</MenuItem>
        ))}
        {menuCtx && menuCtx.columnId !== 'available' && (
          <MenuItem onClick={() => {
            const fid = menuCtx.featureId;
            setAssignedByTier(prev => {
              const next: Record<string, string[]> = { ...prev };
              const s = new Set(next[menuCtx.columnId] || []); s.delete(fid); next[menuCtx.columnId] = Array.from(s);
              return next;
            });
            setSelection(prev => {
              const next: Record<string, Set<string>> = { ...prev };
              const set = new Set(next[menuCtx.columnId] || []);
              set.delete(fid);
              if (set.size === 0) delete next[menuCtx.columnId]; else next[menuCtx.columnId] = set;
              const anyLeft = Object.values(next).some(s => (s?.size || 0) > 0);
              if (!anyLeft) setSelectionScope(null);
              return next;
            });
            closeMenu();
          }}>Remove from this tier</MenuItem>
        )}
      </Menu>

      <Drawer anchor="right" open={addOnsOpen} onClose={() => setAddOnsOpen(false)}>
        <Box sx={{ width: 360, p: 2 }} role="presentation">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">Manage Add-Ons</Typography>
              <Chip size="small" label={addOnCount} />
            </Stack>
            <Button size="small" onClick={() => refetchAddons()}>Refresh</Button>
          </Stack>
          {!selectedProduct ? (
            <Typography variant="body2" color="text.secondary">Select a product</Typography>
          ) : (
            <>
              {(addonsData || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No add-ons for this product.</Typography>
              ) : (
                <List>
                  {(addonsData || []).map((a: any) => (
                    <ListItem key={a.id} secondaryAction={
                      <Button size="small" onClick={async () => {
                        try {
                          await unmarkAddOn({ productId: a.productId, featureId: a.featureId }).unwrap();
                          await refetchAddons();
                          toast.success('Unmarked add-on');
                        } catch (err: any) {
                          const msg = err?.data?.title || err?.data || err?.message || 'Failed to unmark add-on';
                          toast.error(typeof msg === 'string' ? msg : 'Failed to unmark add-on');
                        }
                      }}>Unmark</Button>
                    }>
                      <ListItemText primary={a.featureName} secondary={a.notes} />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default ProductsPage;
